export interface YUVDataLayout {
	yOffset: number
	yLength: number

	uOffset: number
	uLength: number

	vOffset: number
	vLength: number
}

export interface RGBPlanesDataLayout {
	rOffset: number
	rLength: number

	gOffset: number
	gLength: number

	bOffset: number
	bLength: number
}

export interface PixelData {
	data: Uint8Array | Uint16Array | Float32Array
	yuvDataLayout?: YUVDataLayout
	rgbPlanesDataLayout?: RGBPlanesDataLayout
	width: number
	height: number
	colorSpace: PredefinedColorSpace
}

export function toNativeImageData({ data, width, height, colorSpace }: PixelData) {
	if (!(data instanceof Uint8Array)) {
		throw new Error("Native ImageData can only accept Uint8Array")
	}

	return new ImageData(
		new Uint8ClampedArray(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength),
		width,
		height,
		{ colorSpace },
	)
}

export function imageDataToBitmap(
	imageData: PixelData | ImageData,
	crop?: { x: number; y: number; w: number; h: number },
) {
	if (!(imageData instanceof ImageData)) {
		imageData = toNativeImageData(imageData)
	}

	// Crop to specified coordinates and size
	if (crop) {
		return createImageBitmap(imageData, crop.x, crop.y, crop.w, crop.h, {
			colorSpaceConversion: "none",
		})
	}

	return createImageBitmap(imageData, { colorSpaceConversion: "none" })
}

let bitmapCanvas: OffscreenCanvas | null = null
let bitmapCanvasColorSpace: PredefinedColorSpace | null = null
let bitmapContext: ImageBitmapRenderingContext | null

export async function imageDataToCanvas(imageData: PixelData | ImageData) {
	const imageBitmap = await imageDataToBitmap(imageData)

	if (!bitmapContext || !bitmapCanvas || bitmapCanvasColorSpace !== imageData.colorSpace) {
		bitmapCanvas = new OffscreenCanvas(1, 1)
		bitmapCanvasColorSpace = imageData.colorSpace
		bitmapContext = bitmapCanvas.getContext("bitmaprenderer", {
			colorSpace: imageData.colorSpace,
		})
	}

	// It seems like we don't need to update the canvas size for convertToBlob to work!?
	// canvas.width = imageBitmap.width
	// canvas.height = imageBitmap.height

	if (!bitmapContext) {
		throw new Error("Could not create canvas context")
	}

	bitmapContext.transferFromImageBitmap(imageBitmap)

	imageBitmap.close()

	return bitmapCanvas
}

let bitmapCanvas2D: OffscreenCanvas | null = null
let bitmapContext2D: OffscreenCanvasRenderingContext2D | null

export function bitmapToImageData(imageBitmap: ImageBitmap): ImageData {
	if (!bitmapCanvas2D || !bitmapContext2D) {
		bitmapCanvas2D = new OffscreenCanvas(1, 1)
		bitmapContext2D = bitmapCanvas2D.getContext("2d")
	}

	if (!bitmapContext2D) {
		throw new Error("Could not create canvas context")
	}

	bitmapCanvas2D.width = imageBitmap.width
	bitmapCanvas2D.height = imageBitmap.height

	bitmapContext2D.drawImage(imageBitmap, 0, 0)

	const imageData = bitmapContext2D.getImageData(
		0,
		0,
		bitmapCanvas2D.width,
		bitmapCanvas2D.height,
	)

	return imageData
}

export async function bitmapToWebPData(
	imageBitmap: ImageBitmap,
	options: {
		quality?: number
	},
): Promise<Uint8Array> {
	const { quality = 92 } = options

	if (!bitmapCanvas2D || !bitmapContext2D) {
		bitmapCanvas2D = new OffscreenCanvas(1, 1)
		bitmapContext2D = bitmapCanvas2D.getContext("2d")
	}

	if (!bitmapContext2D) {
		throw new Error("Could not create canvas context")
	}

	bitmapCanvas2D.width = imageBitmap.width
	bitmapCanvas2D.height = imageBitmap.height

	// Put imageData on canvas
	bitmapContext2D.drawImage(imageBitmap, 0, 0, imageBitmap.width, imageBitmap.height)

	// Convert to WebP
	const webpBlob = await bitmapCanvas2D.convertToBlob({
		type: "image/webp",
		quality: quality / 100,
	})

	// Get WebP data
	const webpData = new Uint8Array(await webpBlob.arrayBuffer())

	return webpData
}

export async function webpDataToBitmap(webpData: Uint8Array) {
	const blob = new Blob([webpData as unknown as BlobPart], { type: "image/webp" })
	const imageBitmap = await createImageBitmap(blob)
	return imageBitmap
}

/**
 * Convert ImageData to base64 string (without data URL header)
 * @param imageData - ImageData to convert
 * @param format - Image format ("png" or "jpeg")
 * @param quality - Quality for JPEG (0-1), default 0.8
 * @returns Base64-encoded image string without header
 */
export function imageDataToBase64(
	imageData: ImageData,
	format: "png" | "jpeg" = "jpeg",
	quality = 0.8,
): string {
	const canvas = document.createElement("canvas")
	canvas.width = imageData.width
	canvas.height = imageData.height
	const context = canvas.getContext("2d")
	if (!context) {
		throw new Error("Canvas 2D context unavailable")
	}
	context.putImageData(imageData, 0, 0)

	// Use JPEG with compression for much smaller file sizes
	const mimeType = format === "png" ? "image/png" : "image/jpeg"
	const dataUrl =
		format === "png" ? canvas.toDataURL(mimeType) : canvas.toDataURL(mimeType, quality)

	console.log(
		`📏 Image converted to ${format.toUpperCase()}${
			format === "jpeg" ? ` (quality: ${quality})` : ""
		}, size: ~${Math.round(dataUrl.length / 1024)}KB`,
	)
	return dataUrl.split(",")[1] ?? ""
}
